import { motion } from 'motion/react'
import { useMemo } from 'react'
import type { DayItem } from '../../api/types'
import { AsyncPanel, PanelSkeleton, Shimmer } from '../ui/AsyncPanel'
import { TOKENS } from '../ui/dashboard-primitives'
import { useAttendancePanel } from './panelStates'

const WEEKS = 27
const ON_TARGET = 2
const EASE = [0.16, 1, 0.3, 1] as const

function mondayOf(d: Date): Date {
  const m = new Date(d)
  const day = (m.getDay() + 6) % 7 // Mon=0
  m.setDate(m.getDate() - day)
  m.setHours(0, 0, 0, 0)
  return m
}

interface Week {
  start: Date
  count: number
}

/**
 * The last 27 weeks of sessions, one column a week.
 *
 * Its label stays up in every state, so a reader always knows which panel is
 * loading or failed. Below it, `AsyncPanel` decides between the skeleton, the
 * strip and the house error (Spec 2 D5, #169): before that, a failed or paused
 * request drew every week as missed and read "0 of 27 weeks on target". An
 * account with no sessions yet still draws the strip, because zero is real
 * data; there is no separate empty state.
 */
export function WeeklyAttendance() {
  const attendance = useAttendancePanel()

  return (
    <div>
      <p
        className="text-[0.5625rem] tracking-[0.28em] uppercase"
        style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
      >
        Attendance · {WEEKS} weeks
      </p>
      <AsyncPanel
        sources={attendance.sources}
        hasContent={attendance.hasContent}
        subject="Your attendance"
        skeleton={<AttendanceSkeleton />}
      >
        {() => <AttendanceStrip items={attendance.data?.items ?? []} />}
      </AsyncPanel>
    </div>
  )
}

/** The strip's footprint while the request is out: a calm shimmer where the
 *  columns go and one where the summary line goes, never 27 empty columns,
 *  which would read as 27 missed weeks. */
function AttendanceSkeleton() {
  return (
    <PanelSkeleton label="Loading attendance">
      <div className="mt-4">
        <Shimmer height={28} />
      </div>
      <div className="mt-3.5 w-44 max-w-full">
        <Shimmer height={12} />
      </div>
    </PanelSkeleton>
  )
}

function AttendanceStrip({ items }: { items: DayItem[] }) {
  const weeks = useMemo<Week[]>(() => {
    const thisMonday = mondayOf(new Date())
    const starts: Week[] = []
    for (let i = WEEKS - 1; i >= 0; i--) {
      const s = new Date(thisMonday)
      s.setDate(s.getDate() - i * 7)
      starts.push({ start: s, count: 0 })
    }
    const earliest = starts[0].start.getTime()
    for (const item of items) {
      const t = mondayOf(new Date(`${item.date.slice(0, 10)}T00:00`)).getTime()
      if (t < earliest) continue
      const idx = Math.round((t - earliest) / (7 * 86_400_000))
      if (idx >= 0 && idx < WEEKS) starts[idx].count++
    }
    return starts
  }, [items])

  const onTarget = weeks.filter((w) => w.count >= ON_TARGET).length
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

  return (
    <>
      <div className="grid gap-1 items-end mt-4" style={{ gridTemplateColumns: `repeat(${WEEKS}, 1fr)` }}>
        {weeks.map((w, i) => {
          const now = i === WEEKS - 1
          const miss = w.count < ON_TARGET
          const color = now
            ? TOKENS.amber
            : miss
              // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
              ? 'rgba(198,110,77,0.45)'
              // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
              : 'rgba(245,237,225,0.30)'
          return (
            <div key={i} className="flex flex-col-reverse gap-[3px]" style={{ minHeight: 9 }}>
              {w.count === 0 ? (
                // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                <span className="block h-px rounded-sm" style={{ background: 'rgba(198,110,77,0.4)' }} />
              ) : (
                Array.from({ length: w.count }).map((_, d) => (
                  <motion.u
                    key={d}
                    className="block h-[9px] rounded-sm no-underline origin-bottom"
                    style={{ background: color }}
                    initial={{ opacity: 0, scaleY: 0.3 }}
                    whileInView={{ opacity: 1, scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, ease: EASE, delay: i * 0.022 }}
                  />
                ))
              )}
            </div>
          )
        })}
      </div>

      <div className="flex justify-between mt-2.5 text-[0.5625rem] tracking-[0.14em] uppercase" style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamFaint }}>
        <span>{fmt(weeks[0].start)}</span>
        <span>{fmt(weeks[Math.floor(WEEKS / 2)].start)}</span>
        <span>{fmt(weeks[WEEKS - 1].start)}</span>
      </div>

      <p className="mt-2 text-[0.8125rem]" style={{ color: TOKENS.creamSoft }}>
        <b style={{ color: TOKENS.cream }}>{onTarget} of {WEEKS}</b> weeks on target.
      </p>
    </>
  )
}
